import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import DetailSection from '@components/common/DetailSection';
import Badge from '@components/common/Badge';
import DraftEvidenceActions from './DraftEvidenceActions';
import { useAuth } from '@contexts/AuthContext';
import { evidenceService } from '@services';
import { getDraftEvidencePermissions } from '@utils/permissionUtils';
import { priorityToVariant } from '@utils/badgeMappers';
import { formatDateOnly } from '@utils/dateUtils';
import { DRAFT_EVIDENCE_LABEL, findDraftEvidence } from '@utils/evidenceVisibility';
import type { AssessmentItem } from '@app-types';
import styles from './DraftEvidenceSection.module.css';

interface DraftEvidenceSectionProps {
  items: AssessmentItem[];
}

interface DraftEvidenceRowProps {
  item: AssessmentItem;
}

const DraftEvidenceRow: React.FC<DraftEvidenceRowProps> = ({ item }) => {
  const { user } = useAuth();
  // Same key as the Process row, so both views share one request and one invalidation.
  const { data } = useQuery({
    queryKey: ['assessment-item-evidence', item.id],
    queryFn: () => evidenceService.getEvidenceForItem(item.id)
  });
  const draft = findDraftEvidence(data?.evidence ?? []);

  return (
    <li className={styles.draftItem} data-test-id="draft-evidence-section-item">
      <div className={styles.draftInfo}>
        <div className={styles.processLine}>
          <span className={styles.processName}>{item.process_name}</span>
          <Badge variant={priorityToVariant(item.process_priority)}>{item.process_priority}</Badge>
        </div>
        <div className={styles.draftLabel}>
          <FileText size={14} />
          <span>{DRAFT_EVIDENCE_LABEL}</span>
        </div>
        {draft && (
          <div className={styles.draftMeta} data-test-id="draft-evidence-section-meta">
            {draft.evidence_name} · Uploaded by {draft.uploaded_by_name} on {formatDateOnly(draft.created_at)}
          </div>
        )}
      </div>
      <div className={styles.draftActions}>
        <DraftEvidenceActions
          assessmentItemId={item.id}
          processName={item.process_name}
          permissions={getDraftEvidencePermissions(user, draft?.uploaded_by_id)}
        />
      </div>
    </li>
  );
};

/**
 * Draft Evidence on an assessment, for the Admin / Program Lead page, which has no
 * Process list (ADO-1148). They may only delete a stuck draft, e.g. when the
 * contract has no Contract Manager or Owner to submit it. Hidden when there is none.
 */
const DraftEvidenceSection: React.FC<DraftEvidenceSectionProps> = ({ items }) => {
  const draftItems = items.filter((item) => item.evidence_status === 'draft');

  if (draftItems.length === 0) {
    return null;
  }

  return (
    <DetailSection title="Draft Evidence">
      <div data-test-id="draft-evidence-section">
        <p className={styles.description}>
          Uploaded by the Assessor and awaiting submission by the Contract Manager or Owner.
        </p>
        <ul className={styles.draftList}>
          {draftItems.map((item) => (
            <DraftEvidenceRow key={item.id} item={item} />
          ))}
        </ul>
      </div>
    </DetailSection>
  );
};

export default DraftEvidenceSection;
